import CryptoJS from "crypto-js";
import { COURSE, LESSON, PLACEHOLDER_IMGS, USER } from "./Enums";

const key = CryptoJS.enc.Utf8.parse(process.env.REACT_APP_CRYPTO_KEY);
const iv = CryptoJS.enc.Utf8.parse(process.env.REACT_APP_CRYPTO_KEY);

/**
 * @author GM
 * @description Returns the Initials from a Name
 */
export const GetNameInitials = (Name) => {
  try {
    const arrName = Name.trim().split(" ").filter(Boolean);
    if (arrName.length >= 2) {
      return `${arrName[0][0]}${arrName[1][0]}`.toUpperCase();
    } else if (arrName.length === 1) {
      return arrName[0][0].toUpperCase();
    } else {
      return "";
    }
  } catch (error) {
    return "";
  }
};

/**
 * @author PL
 * @description Checks if a string is empty or not
 */
export const IsStringEmpty = (text) => {
  return !text || text?.trim().length === 0;
};

/**
 * @author GM
 * @date 16-02-2023
 * @description Validates the Email address
 */
export const IsEmailValid = (emailAddress) => {
  const emailRegexString =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/;
  return emailRegexString.test(emailAddress);
};

/**
 * @author PL
 * @date 18-05-2023
 * @description Validates phone numbers
 */
export const IsPhoneNumberValid = (phone) => {
  const phoneRegexString = /^\d{7,15}$/;
  return phoneRegexString.test(phone);
};

/**
 * @author GM
 * @date 04/01/2023
 * @description Validates the Password for NIST requirement
 */
export const IsPasswordValid = (passowrd) => {
  const NIStPasswordRegexString =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*\W).{8,}$/;
  return NIStPasswordRegexString.test(passowrd);
};

/**
 * @author GM
 * @date 12-04-2023
 * @description Get logged in user id
 */
export const GetLoggedInUserId = () => {
  const eUser = GetLocalStorageData("eUser");
  return eUser?.userGUID ?? "";
};

export const GetUserGUID = () => {
  const eUser = GetLocalStorageData("eUser");
  return eUser?.userGUID ?? "0";
};

/**
 * @author PL
 * @date 25-05-2023
 * @description Checks if the logged in user is a trial user
 */
export const IsLoggedInUserTrial = () => {
  return GetLocalStorageData("eUser")?.userRole === USER.ROLE.TRIAL;
};

export const IsLoggedInUserAdmin = () => {
  return GetLocalStorageData("eUser")?.userRole === USER.ROLE.ADMIN;
};

/**
 * @author GM
 * @date 11-04-2023
 * @description Add data to local storage in encrypted form
 */
export const AddLocalStorageData = (storageKey, value) => {
  const encryptedValue = EncryptUsingAES256(JSON.stringify(value));
  const encryptedKey = EncryptUsingAES256(storageKey);

  if (localStorage.getItem(storageKey)) {
    localStorage.removeItem(storageKey);
  }
  localStorage.setItem(encryptedKey, encryptedValue);
};

/**
 * @author GM
 * @date 11-04-2023
 * @description Get decrypted data from local storage
 */
export const GetLocalStorageData = (storageKey) => {
  const encryptedKey = EncryptUsingAES256(storageKey);
  const encryptedValue = localStorage.getItem(encryptedKey) ?? undefined;

  try {
    if (encryptedValue) {
      return JSON.parse(DecryptUsingAES256(encryptedValue));
    }
  } catch (error) {}

  return encryptedValue;
};

/**
 * @author GM
 * @date 11-04-2023
 * @description Remove local storage data
 */
export const RemoveLocalStorageData = (storageKey) => {
  const encryptedKey = EncryptUsingAES256(storageKey);
  localStorage.removeItem(encryptedKey);
};

/**
 * @author GM
 * @date 11-04-2023
 * @description Encryption using CryptoJS
 */
export const EncryptUsingAES256 = (text) => {
  const encrypted = CryptoJS.AES.encrypt(CryptoJS.enc.Utf8.parse(text), key, {
    iv,
    keySize: 128 / 8,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  return encrypted.toString();
};

/**
 * @author GM
 * @date 11-04-2023
 * @description Decryption using CryptoJS
 */
export const DecryptUsingAES256 = (decString) => {
  const decrypted = CryptoJS.AES.decrypt(decString, key, {
    iv,
    keySize: 128 / 8,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  return decrypted.toString(CryptoJS.enc.Utf8);
};

/**
 * @author PL
 * @date 12-05-2023
 * @description Converts a file to a Base64 string
 */
export const GetBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

/**
 * @author PL
 * @description Checks if the lesson content type is interactive
 */
export const IsContentInteractive = (contentType) => {
  const { PARAGRAPH, QUOTE, ACTION_BUTTON, SORTING_GAME, MATCHING_GAME } =
    LESSON.CONTENT_TYPE;
  return (
    contentType !== PARAGRAPH &&
    contentType !== QUOTE &&
    contentType !== ACTION_BUTTON &&
    contentType !== SORTING_GAME &&
    contentType !== MATCHING_GAME
  );
};

/**
 * @author VK
 * @date 17-07-2023
 * @description Valid Name String
 */
export const IsNameStringValid = (name) => {
  const nameRegexString = /^(?!.*[A-Z]{2})[A-Za-z]+$/;
  return nameRegexString.test(name);
};

/**
 * @author PL
 * @date 21-07-2023
 * @description Checks if an object is empty or not
 */
export const IsObjectEmpty = (objectName) => {
  return (
    objectName &&
    Object.keys(objectName).length === 0 &&
    objectName.constructor === Object
  );
};

/**
  @author PL
  @date 27-07-2023
  @description Display placeholder in place if there's an error loading an image
*/
export const HandleImageErr = (e, placeholderImg = PLACEHOLDER_IMGS.ERROR) => {
  e.currentTarget.onerror = null; // prevents looping
  e.target.src = placeholderImg;
};

/**
 * @author PL
 * @date 01-08-2023
 * @description Converts time in seconds into hours, minutes, seconds
 */
export const ConvertSecondsToHHMMSS = (secs) => {
  if (!secs) {
    return { hours: 0, minutes: 0, seconds: 0 };
  }

  let totalSeconds = secs;

  const hours = Math.floor(totalSeconds / 3600);
  totalSeconds %= 3600;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return { hours, minutes, seconds };
};

/**
 * @author PL
 * @date 18-09-2023
 * @description Delays the execution of a function
 */
export const Debounce = (fn, delay) => {
  let timer = null;

  return (...args) => {
    clearTimeout(timer);

    timer = setTimeout(() => {
      fn(...args);
    }, delay);
  };
};

/**
 * @author PL
 * @description Convert date string into format YYYY-MM-DD
 */
export const GetDateYYMMDD = (dateStr, separator = "-") => {
  // Use the dateStr for the date portion
  const date = dateStr ? new Date(dateStr) : new Date();

  return `${date.getFullYear()}${separator}${(date.getMonth() + 1)
    .toString()
    .padStart(2, "0")}${separator}${date
    .getDate()
    .toString()
    .padStart(2, "0")}`;
};

/**
 * @author PL
 * @description Convert date string into format DD-MM-YYYY
 */
export const GetDateDDMMYY = (str, separator = "-") => {
  if (IsStringEmpty(str)) {
    return str;
  }

  const date = str ? new Date(str) : new Date(),
    mnth = `0${date.getMonth() + 1}`.slice(-2),
    day = `0${date.getDate()}`.slice(-2);

  return [day, mnth, date.getFullYear()].join(separator);
};

/**
 * @author PL
 * @date 17-01-2024
 * @description Returns the month text based on index (0 to 11)
 */
export const GetMonthText = (index) => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  if (index < 0) {
    return months[0];
  }

  if (index >= months.length) {
    return months[months.length - 1];
  }

  return months[index];
};

/**
 * @author PL
 * @date 17-01-2024
 * @description Convert number to its ordinal form e.g. 1st, 2nd, 3rd, etc.
 */
export const GetOrdinalNumber = (number) => {
  if (number > 3 && number < 21) {
    return number + "th";
  }

  switch (number % 10) {
    case 1:
      return number + "st";
    case 2:
      return number + "nd";
    case 3:
      return number + "rd";
    default:
      return number + "th";
  }
};

/**
 * @author PL
 * @date 10/01/2024
 * @description Returns promise with loaded image, otherwise rejects with error
 * */
export const LoadImageAsync = async (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.src = url;

    // Define an event handler for when the image is successfully loaded
    img.onload = function () {
      // Resolve the promise with the loaded image
      resolve(img);
    };

    // Define an event handler for when there's an error loading the image
    img.onerror = function () {
      // Reject the promise with an error
      reject(new Error("Error loading image"));
    };
  });
};

export const ShuffleArray = (array) => {
  let currentIndex = array.length,
    randomIndex;

  // While there remain elements to shuffle.
  while (currentIndex > 0) {
    // Pick a remaining element.
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    // And swap it with the current element.
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex],
      array[currentIndex],
    ];
  }

  return [...array];
};

/**
 * @author VK
 * @date 07-08-2023
 * @description To get current year
 */
export const GetCurrentYear = () => {
  const date = new Date();
  return date.getFullYear();
};

/**
 * @author VK
 * @date 29-03-2024
 * @description To get current Date in DD-MM-YYYY
 */
export const GetCurrentDateDDMMYYYY = () => {
  const currentDate = new Date();
  const day = currentDate.getDate().toString().padStart(2, "0"); // Ensure two digits for day
  const month = (currentDate.getMonth() + 1).toString().padStart(2, "0"); // Months are zero-indexed
  const year = currentDate.getFullYear().toString();
  return `${day}-${month}-${year}`;
};

export const verifyValidImageFile = (file) => {
  if (!file?.type || !file?.name || !file?.size) {
    return false;
  }

  // 1. Check if type property startwith "image/"
  if (!file.type.startsWith("image/")) {
    return false;
  }

  // 2. Check for valid image extensions
  const validImageFormats = [
    "bmp",
    "gif",
    "jpeg",
    "jpg",
    "jfif",
    "png",
    "webp",
  ];

  // Regex to get file extension
  const fileFormatRegex = /(?:\.([^.]+))?$/;
  const fileFormat = fileFormatRegex.exec(file.name)[1];

  return validImageFormats.some((f) => f === fileFormat.toLowerCase());
};

export const getCourseTypeName = (courseTypeId) => {
  switch (courseTypeId) {
    case COURSE.TYPE.SELF_DIRECTED:
      return "Self-directed";
    case COURSE.TYPE.LIVE:
      return "Live";
    case COURSE.TYPE.EXTERNAL:
      return "External";
    default:
      return "Unknown Course Type";
  }
};

export const getRequirementTypeName = (reqTypeId) => {
  switch (reqTypeId) {
    case COURSE.REQUIREMENT_TYPE.PRIORITY:
      return "Priority";
    case COURSE.REQUIREMENT_TYPE.MANDATORY:
      return "Mandatory";
    case COURSE.REQUIREMENT_TYPE.RECOMMENDED:
      return "Recommended";
    default:
      return "N/A";
  }
};

export const GetBrowserTimeZone = () => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return timezone;
};

// Method to convert the date to the specified time zone
export const ConvertToLocalTimeZone = (currentDate, timeZone) => {
  return new Date(currentDate.toLocaleString("en-US", { timeZone }));
};

// Helper function to parse 'dd/MM/yyyy - hh:mm A' to Date object
export const ParseAssignedDate = (dateString) => {
  const [datePart, timePart] = dateString.split(", ");
  const [day, month, year] = datePart.split("/");
  const [time, period] = timePart.split(" ");

  let [hours, minutes] = time.split(":");
  if (period === "PM" && hours !== "12") {
    hours = parseInt(hours, 10) + 12; // Convert PM to 24-hour format
  } else if (period === "AM" && hours === "12") {
    hours = 0; // Handle midnight case
  }
  // Create a new Date object using the parsed values
  return new Date(year, month - 1, day, hours, minutes);
};

/**
 * @author PL
 * @description Shortens text to some minimumn length if possible and add ellipses (...)
 */
export const ShortenText = (text, minLength) => {
  const ellipsis = "...";

  if (text.length > minLength) {
    return text.slice(0, minLength - ellipsis.length).concat(ellipsis);
  }
  return text;
};

export function StripHtml(html) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
}

export function LogToConsole(data) {
  if (!data) return data;

  return JSON.parse(JSON.stringify(data));
}

export const isAndroidDevice = () => /Android/i.test(navigator.userAgent);
